import { Link } from 'react-router-dom';
import { useApi } from '../lib/useApi.ts';
import { useCanWrite } from '../lib/auth.tsx';
import { post } from '../lib/api.ts';
import { shortDate } from '../lib/format.ts';
import { Badge, Button, Card, EmptyState, Skeleton } from '../components/ui/index.ts';
import { AlertIcon, CheckIcon, ClockIcon } from '../components/icons.tsx';
import { cx } from '../lib/cx.ts';

interface Task {
  id: number;
  lead_id: number | null;
  lead_name: string | null;
  practice_name: string | null;
  title: string;
  due_at: string | null;
  source: string;
  auto_created: boolean;
}

interface Response {
  tasks: Task[];
  total: number;
  counts: { open: string; no_next_event: string; overdue: string };
  headlineSource: string;
}

/**
 * The open loop that matters most gets the top of the page to itself.
 *
 * CLAUDE.md calls a ran call with no next event "the documented leak", and
 * SPEC 3.6 scores the operator on it. Burying it in a task list next to
 * "client untouched for 15 days" would be the wrong answer, so it is split
 * out and everything else sits underneath.
 */
export default function Today() {
  const canWrite = useCanWrite();
  const { data, error, loading, reload } = useApi<Response>('/tasks?limit=200');

  async function complete(id: number) {
    await post(`/tasks/${id}/done`, {});
    reload();
  }

  const headline = (data?.tasks ?? []).filter((task) => task.source === data?.headlineSource);
  const rest = (data?.tasks ?? []).filter((task) => task.source !== data?.headlineSource);
  const noNextEvent = Number(data?.counts.no_next_event ?? 0);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold tracking-tighter">Today</h1>
        <p className="mt-0.5 text-xs text-fg-subtle">
          Open loops, found automatically each morning.
        </p>
      </header>

      {error && <p className="text-sm text-lost">{error}</p>}
      {loading && <Skeleton className="h-40 w-full" />}

      {!loading && data && (
        <section
          className={cx(
            'rounded-lg border p-5',
            noNextEvent > 0 ? 'border-risk/30 bg-risk-subtle' : 'border-border bg-surface',
          )}
        >
          <div className="flex items-start gap-3">
            {noNextEvent > 0 ? (
              <AlertIcon size={18} className="mt-0.5 shrink-0 text-risk" />
            ) : (
              <CheckIcon size={18} className="mt-0.5 shrink-0 text-won" />
            )}
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-medium text-fg">
                {noNextEvent > 0
                  ? `${noNextEvent} call${noNextEvent === 1 ? '' : 's'} ran with nobody owning the next step`
                  : 'Every call that ran has a next step owned'}
              </h2>
              <p className="mt-0.5 text-xs text-fg-subtle">
                {noNextEvent > 0
                  ? 'This is the leak. Each of these is a conversation that happened and then stopped.'
                  : 'Nothing is sitting open. This is the number to keep at zero.'}
              </p>

              {headline.length > 0 && (
                <ul className="mt-4 space-y-2">
                  {headline.map((task) => (
                    <li
                      key={task.id}
                      className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface px-3 py-2"
                    >
                      <div className="min-w-0">
                        {task.lead_id ? (
                          <Link
                            to={`/leads/${task.lead_id}`}
                            className="text-sm font-medium text-fg transition hover:text-accent-text"
                          >
                            {task.lead_name}
                          </Link>
                        ) : (
                          <span className="text-sm font-medium text-fg">{task.lead_name}</span>
                        )}
                        <p className="mt-0.5 truncate text-xs text-fg-subtle">{task.title}</p>
                      </div>
                      {canWrite && (
                        <Button size="sm" variant="secondary" onClick={() => void complete(task.id)}>
                          Done
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>
      )}

      {!loading && data && (
        <section>
          <div className="mb-3 flex items-baseline gap-2">
            <h2 className="text-sm font-medium text-fg">Everything else</h2>
            <span className="text-2xs text-fg-subtle">{rest.length} open</span>
          </div>

          {rest.length === 0 ? (
            <Card>
              <EmptyState
                icon={<CheckIcon size={18} />}
                title="Nothing else is open"
                description="Assessments are out, nobody has been ghosted, no payment is overdue."
                compact
              />
            </Card>
          ) : (
            <div className="space-y-2">
              {rest.map((task) => {
                const overdue = task.due_at !== null && new Date(task.due_at) < new Date();
                return (
                  <Card key={task.id}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          {task.lead_id ? (
                            <Link
                              to={`/leads/${task.lead_id}`}
                              className="text-sm font-medium text-fg transition hover:text-accent-text"
                            >
                              {task.lead_name}
                            </Link>
                          ) : (
                            <span className="text-sm font-medium text-fg">Task</span>
                          )}
                          {overdue && <Badge tone="risk">overdue</Badge>}
                          {!task.auto_created && <Badge tone="neutral">manual</Badge>}
                        </div>
                        <p className="mt-0.5 text-xs text-fg-muted">{task.title}</p>
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        {task.due_at && (
                          <span className="numeric flex items-center gap-1 text-xs text-fg-subtle">
                            <ClockIcon size={11} />
                            {shortDate(task.due_at)}
                          </span>
                        )}
                        {canWrite && (
                          <Button size="sm" variant="ghost" onClick={() => void complete(task.id)}>
                            Done
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
